import React, { useEffect, useState } from 'react';
import { historyService } from '../../services/historyService';
import { authService } from '../../services/authService';
import { dayLabel } from '../../lib/shareViews';
import { formatMoscowDate } from '../../lib/moscowDate';

/**
 * «Мои ссылки» в личных метриках: открытых ссылок → открытий → регистраций.
 * Отклик на «поделиться»: человек видит, что его ссылку открыли и кто после
 * этого пришёл. Спецификация: docs/superpowers/specs/2026-10-10-share-views-design.md.
 *
 * 🔴 Денег здесь нет и не будет (решение владельца 10.10.2026): продукт
 * отвечает «чей клиент», долю офиса и комиссию сотруднику не показывает.
 *
 * Два источника, оба best-effort: ссылки и открытия — bit-history
 * (`/me/shares`), регистрации — bit-auth (`/me/referrals`). Не ответил один —
 * его числа «—»; не ответили оба или считать ещё нечего и один молчит —
 * строки нет, выдумывать нули не из чего.
 */

const NUM = 'font-display text-[28px] font-extrabold leading-none tracking-[-0.03em]';
const CAPTION = 'mt-1 text-[12px] text-paper-ink-3';

const shown = (n) => (n === null ? '—' : String(n));

export default function MyLinks() {
  // undefined — едет, null — не ответил, объект — ответ.
  const [shares, setShares] = useState(undefined);
  const [referrals, setReferrals] = useState(undefined);
  const [whoOpen, setWhoOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    historyService.myShares()
      .then((d) => { if (alive) setShares(d || null); })
      .catch(() => { if (alive) setShares(null); });
    authService.myReferrals()
      .then((d) => { if (alive) setReferrals(d || null); })
      .catch(() => { if (alive) setReferrals(null); });
    return () => { alive = false; };
  }, []);

  if (shares === undefined || referrals === undefined) return null;

  const live = shares ? shares.live || 0 : null;
  const views = shares ? shares.views || 0 : null;
  const invited = referrals ? referrals.invited || 0 : null;
  const people = (referrals && referrals.items) || [];

  if (!live && !views && !invited) {
    // Нули от обоих источников — человек ещё не делился: вместо нулей подсказка.
    if (!shares || !referrals) return null;
    return (
      <div data-testid="my-links-hint"
           className="shrink-0 bg-paper-card rounded-card px-6 py-4 flex flex-wrap items-center gap-x-8 gap-y-2">
        <div className="min-w-[150px] text-[14px] font-semibold text-paper-ink">Мои ссылки</div>
        <div className="text-[13px] text-paper-ink-2 max-w-[70ch]">
          Отправьте протокол ссылкой — здесь будет видно, сколько раз её открыли и кто после
          этого зарегистрировался. Откройте запись в Истории и нажмите «Дать ссылку».
        </div>
      </div>
    );
  }

  const lastView = shares ? dayLabel(shares.last_view_at) : '';

  return (
    <div data-testid="my-links" className="shrink-0 bg-paper-card rounded-card px-6 py-4">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
        <div className="min-w-[150px]">
          <div className="text-[14px] font-semibold text-paper-ink">Мои ссылки</div>
          <div className="text-[12px] text-paper-ink-3">записи, которыми вы поделились</div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
          <div>
            <div data-testid="my-links-live" className={`${NUM} text-paper-ink`}>{shown(live)}</div>
            <div className={CAPTION}>открытых ссылок</div>
          </div>
          <span aria-hidden="true" className="text-[18px] text-paper-hint">→</span>
          <div>
            <div data-testid="my-links-views" className={`${NUM} text-paper-ink`}>{shown(views)}</div>
            <div className={CAPTION}>открытий</div>
          </div>
          <span aria-hidden="true" className="text-[18px] text-paper-hint">→</span>
          <div>
            <div data-testid="my-links-invited"
                 className={`${NUM} ${invited ? 'text-paper-accent' : 'text-paper-hint'}`}>
              {shown(invited)}
            </div>
            <div className={CAPTION}>зарегистрировались</div>
          </div>
        </div>
        {(lastView || people.length > 0) && (
          <div className="ml-auto text-right text-[12px] text-paper-ink-2">
            {lastView && <div>последнее открытие — {lastView}</div>}
            {people.length > 0 && (
              <button type="button" aria-expanded={whoOpen} onClick={() => setWhoOpen((v) => !v)}
                      className="text-paper-ink underline underline-offset-[3px]">
                кто зарегистрировался
              </button>
            )}
          </div>
        )}
      </div>
      {whoOpen && people.length > 0 && (
        <div data-testid="my-links-who"
             className="mt-3 flex flex-col gap-1 border-t border-paper-line pt-3 text-[13px]">
          {people.map((p, i) => (
            // Адреса скрыты и могут совпасть (`i***@gmail.com`) — ключ по месту в списке.
            <div key={i} className="flex max-w-[360px] justify-between gap-3">
              <span className="truncate font-mono text-paper-ink">{p.email}</span>
              <span className="whitespace-nowrap text-paper-ink-3">{formatMoscowDate(p.created_at, { year: false })}</span>
            </div>
          ))}
          <div className="mt-1 text-[12px] text-paper-ink-3">
            Адрес скрыт: человек регистрировался в Ньютоне, а не у вас.
          </div>
        </div>
      )}
    </div>
  );
}
